/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the loading component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';

import classNames from 'classnames';

/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface ILoadingProps {
    size: number;
    center: boolean;
}

interface ILoadingStyleProps {
    actualSize: number;
    baseHeight: number;
    baseWidth: number;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme, ILoadingStyleProps>(theme => ({
    bar: {
        position: 'relative',
        height: props => props.baseHeight,
        width: props => props.actualSize
    },
    absoluteCenter: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
    },
    loading: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        justifyContent: 'space-between',
        height: props => props.baseHeight,
        width: props => props.actualSize
    },
    loadingBar: {
        display: 'inline-block',
        borderRadius: 4,
        animationName: '$loading',
        animationDuration: '1s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        height: props => props.baseHeight,
        width: props => props.baseWidth,

        '&:nth-child(1)': {
            background: theme.palette.primary.main,
            animationDelay: '0s'
        },

        '&:nth-child(2)': {
            background: theme.palette.secondary.main,
            animationDelay: '0.09s'
        },

        '&:nth-child(3)': {
            background: theme.palette.primary.main,
            animationDelay: '0.18s'
        },

        '&:nth-child(4)': {
            background: theme.palette.secondary.main,
            animationDelay: '0.27s'
        }
    },
    '@keyframes loading': {
        '0%': {
            transform: 'scale(1)'
        },
        '20%': {
            transform: 'scale(1, 2.2)'
        },
        '40%': {
            transform: 'scale(1)'
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const Loading: React.FunctionComponent<ILoadingProps> = ({
    size,
    center
}) => {
    const actualSize = size ? size : 30;
    const baseHeight = actualSize / 2.2;
    const baseWidth = actualSize / 7.0;

    const classes = useStyles({
        actualSize,
        baseHeight,
        baseWidth
    });

    return (
        <div
            className={classNames(classes.bar, { [classes.absoluteCenter]: center })}
        >
            <div className={classes.loading}>
                <div className={classes.loadingBar} />
                <div className={classes.loadingBar} />
                <div className={classes.loadingBar} />
                <div className={classes.loadingBar} />
            </div>
        </div>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default Loading;
